import { z } from "zod";

/**
 * Listing search and listing detail.
 *
 * MONEY IS ALWAYS INTEGER CENTS on the wire. The website's /listings URL carries
 * dollars because people read it; the website converts before it calls the API.
 */

export const ListingTypeSchema = z.enum(["BY_THE_BED", "ENTIRE_UNIT"]);
export const PropertyTypeSchema = z.enum(["APARTMENT", "HOUSE", "CONDO", "BASEMENT", "TOWNHOUSE"]);
export const SpaceSchema = z.enum(["ENTIRE", "PRIVATE_ROOM", "SHARED_ROOM"]);
export const RoomTypeSchema = z.enum(["PRIVATE", "SHARED"]);
export const GenderRuleSchema = z.enum(["ANY", "MALE_ONLY", "FEMALE_ONLY", "SAME_AS_CURRENT"]);

const cents = z.coerce.number().int().min(0).max(100_000_000);

/**
 * Query string for GET /v1/listings.
 *
 * Same filters, same meaning, as ListingFilters in the website. `q` and
 * `radiusKm` are resolved to id sets inside the API (full-text search, PostGIS)
 * and intersected when both are present.
 */
export const ListingsQuerySchema = z.object({
  city: z.string().trim().min(1).max(120).optional(),
  /** Region code, e.g. "ON". */
  province: z.string().trim().min(1).max(20).optional(),
  regionId: z.uuid().optional(),
  cityId: z.uuid().optional(),
  schoolId: z.uuid().optional(),
  /** Only with schoolId: listings within this many km of the campus. */
  radiusKm: z.coerce.number().min(0.1).max(100).optional(),
  minPrice: cents.optional(),
  maxPrice: cents.optional(),
  gender: z.enum(["MALE", "FEMALE"]).optional(),
  roomType: RoomTypeSchema.optional(),
  space: SpaceSchema.optional(),
  propertyType: PropertyTypeSchema.optional(),
  minCapacity: z.coerce.number().int().min(1).max(50).optional(),
  onlyAvailable: z.enum(["true", "false"]).transform((v) => v === "true").optional(),
  /** Keyword search over title, city and description. */
  q: z.string().trim().min(1).max(200).optional(),
  take: z.coerce.number().int().min(1).max(60).default(24),
});
export type ListingsQuery = z.infer<typeof ListingsQuerySchema>;

/**
 * One search result. `minPriceCents` is the ALL-IN price: the cheapest bed plus
 * the property's mandatory monthly charges. It is the only price a card may
 * show (Competition Act s.74.01(1.1)); `requiredExtrasCents` and `bedPriceCents`
 * exist so the card can say what the price INCLUDES, never to add on top.
 */
export const PropertyCardSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  city: z.string(),
  province: z.string(),
  propertyType: PropertyTypeSchema,
  listingType: ListingTypeSchema,
  totalSlots: z.number().int(),
  availableSlots: z.number().int(),
  bedrooms: z.number().int().nullable(),
  bathrooms: z.number().nullable(),
  minPriceCents: z.number().int().nullable(),
  requiredExtrasCents: z.number().int(),
  bedPriceCents: z.number().int().nullable(),
  coverUrl: z.string().nullable(),
  /** Rounded to 3 decimals (about 100 m). The exact position is never public. */
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  featured: z.boolean(),
});
export type PropertyCard = z.infer<typeof PropertyCardSchema>;

export const ListingsResponseSchema = z.object({ items: z.array(PropertyCardSchema) });
export type ListingsResponse = z.infer<typeof ListingsResponseSchema>;

export const PropertyStatusSchema = z.enum(["DRAFT", "PENDING_REVIEW", "ACTIVE", "PAUSED", "ARCHIVED"]);

export const ListingRoomSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  furnished: z.boolean(),
  roomType: RoomTypeSchema,
  genderRule: GenderRuleSchema,
  /** The bare bed price. The page adds the mandatory charges to show the all-in price. */
  pricePerSlotCents: z.number().int(),
  availableSlots: z.number().int(),
  totalSlots: z.number().int(),
});

export const ListingImageSchema = z.object({
  id: z.uuid(),
  url: z.string(),
  isCover: z.boolean(),
  category: z.string(),
});

export const ListingAmenitySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  isPaid: z.boolean(),
  priceCents: z.number().int().nullable(),
  billing: z.enum(["MONTHLY", "ONE_TIME"]),
  isOptional: z.boolean(),
});

/**
 * Everything the listing page shows that is the same for every visitor.
 *
 * Deliberately has NO street address, postal code, exact coordinates or
 * landlord id: those are shared only once a landlord and tenant connect. What
 * a particular visitor may do (their favourite, their own requests, which
 * rooms their profile fits) is a separate, signed-in reply: ListingViewerSchema.
 */
export const ListingPageSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  city: z.string(),
  province: z.string(),
  status: PropertyStatusSchema,
  propertyType: PropertyTypeSchema,
  listingType: ListingTypeSchema,
  totalSlots: z.number().int(),
  availableSlots: z.number().int(),
  /** Rounded to 3 decimals (about 100 m). */
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  featured: z.boolean(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number().int(),
  availableFrom: z.iso.datetime().nullable(),
  houseRules: z.array(z.string()),
  /** Amenity names from before amenities had prices. Used only when `amenities` is empty. */
  legacyAmenities: z.array(z.string()),
  verificationStatus: z.string(),
  verifications: z.array(z.object({ kind: z.string(), status: z.string(), matchesListing: z.boolean().nullable() })),
  nearestSchool: z.object({ name: z.string() }).nullable(),
  hostName: z.string().nullable(),
  images: z.array(ListingImageSchema),
  videos: z.array(z.object({ id: z.uuid(), url: z.string(), mimeType: z.string() })),
  amenities: z.array(ListingAmenitySchema),
  rooms: z.array(ListingRoomSchema),
});
export type ListingPage = z.infer<typeof ListingPageSchema>;

/**
 * What THIS signed-in visitor may do on a listing. Never cached, never public.
 */
export const ListingViewerSchema = z.object({
  isOwner: z.boolean(),
  isAdmin: z.boolean(),
  favorited: z.boolean(),
  /** Rooms the visitor already has a live request or inquiry on. */
  requestedRoomIds: z.array(z.uuid()),
  /** [roomId, reason] for rooms whose gender rule the visitor's profile cannot satisfy. */
  genderBlocks: z.array(z.tuple([z.uuid(), z.enum(["maleOnly", "femaleOnly", "sameGender"])])),
  compatibility: z
    .object({
      score: z.number(),
      comparedWith: z.number().int(),
      breakdown: z.array(
        z.object({
          key: z.enum(["sleepSchedule", "cleanliness", "noise", "smoking", "guests", "studyHabit"]),
          label: z.string(),
          level: z.enum(["aligned", "mixed", "differs"]),
        }),
      ),
    })
    .nullable(),
  /** True when the visitor has not opted in to roommate matching yet. */
  promptRoommateProfile: z.boolean(),
});
export type ListingViewer = z.infer<typeof ListingViewerSchema>;

export const ListingReviewsSchema = z.object({
  reviews: z.array(
    z.object({
      id: z.uuid(),
      rating: z.number().int(),
      body: z.string().nullable(),
      createdAt: z.iso.datetime(),
      authorName: z.string().nullable(),
      images: z.array(z.object({ id: z.uuid(), url: z.string() })),
      response: z.string().nullable(),
      respondedAt: z.iso.datetime().nullable(),
    }),
  ),
});
export type ListingReviews = z.infer<typeof ListingReviewsSchema>;
